import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserCommunity } from "@/hooks/useUserCommunity";
import { toast } from "sonner";
import supportAgent from "@/assets/support-agent.png";
import { ScrollToTopButton } from "@/components/common/ScrollToTop";

interface TicketMessage {
  id: string;
  message: string;
  sender_id: string;
  created_at: string;
}

interface Ticket {
  id: string;
  subject: string;
  status: string;
  created_at: string;
  messages: TicketMessage[];
}

export function HelpWidget() {
  const { user } = useAuth();
  const { data: community } = useUserCommunity();

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Anonymous user fields
  const [anonName, setAnonName] = useState("");
  const [anonEmail, setAnonEmail] = useState("");

  const isAnonymous = !user;

  // Load tickets & messages for authenticated users
  useEffect(() => {
    if (!open || !user?.id) return;
    const loadTickets = async () => {
      setLoadingTickets(true);
      try {
        const { data: ticketData } = await supabase
          .from("support_tickets")
          .select("id, subject, status, created_at")
          .eq("user_id", user.id)
          .eq("category", "help_widget")
          .order("created_at", { ascending: false })
          .limit(5);

        if (ticketData && ticketData.length > 0) {
          const ticketIds = ticketData.map((t) => t.id);
          const { data: msgData } = await supabase
            .from("ticket_messages")
            .select("id, ticket_id, message, sender_id, created_at")
            .in("ticket_id", ticketIds)
            .order("created_at", { ascending: true });

          const enriched: Ticket[] = ticketData.map((t) => ({
            ...t,
            messages: (msgData || []).filter((m) => m.ticket_id === t.id),
          }));
          setTickets(enriched);
        } else {
          setTickets([]);
        }
      } catch {
        // silently fail
      } finally {
        setLoadingTickets(false);
      }
    };
    loadTickets();
  }, [open, user?.id, sent]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [tickets, open]);

  // Hide widget for authenticated users — they use the in-app support tickets
  if (user) return null;

  const handleSend = async () => {
    if (!message.trim()) return;

    // Anonymous flow
    if (isAnonymous) {
      if (!anonName.trim() || !anonEmail.trim()) {
        toast.error("Please enter your name and email");
        return;
      }
      setSending(true);
      try {
        const { error } = await supabase.functions.invoke("create-contact-ticket", {
          body: {
            name: anonName.trim(),
            email: anonEmail.trim(),
            message: message.trim(),
          },
        });
        if (error) throw error;
        setSent(true);
        setMessage("");
      } catch {
        toast.error("Failed to send message. Please try again.");
      } finally {
        setSending(false);
      }
      return;
    }

    // Authenticated flow
    if (!user?.id) return;
    setSending(true);
    try {
      const { data: ticket, error } = await supabase.from("support_tickets").insert({
        user_id: user.id,
        community_id: community?.id || null,
        subject: message.trim().slice(0, 80),
        description: message.trim(),
        category: "help_widget",
        priority: "normal",
      }).select("id").single();
      if (error) throw error;

      // Add the message to ticket_messages
      if (ticket) {
        await supabase.from("ticket_messages").insert({
          ticket_id: ticket.id,
          sender_id: user.id,
          message: message.trim(),
        });
      }

      // Resend backup pipeline (direct send, bypasses broken queue)
      await supabase.functions.invoke("send-email", {
        body: {
          to: "info@businesspal.tech",
          subject: `🎫 New Support Ticket - Business Pal`,
          type: "new_ticket",
          data: {
            customerName: user.email,
            serviceName: message.trim().slice(0, 80),
            ticketCategory: "help_widget",
          },
        },
      }).catch(() => {});
      // ORIGINAL: Lovable Email pipeline — switch back when pipeline is fixed
      // await supabase.functions.invoke("send-transactional-email", { ... });

      setSent(true);
      setMessage("");
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  // Flatten all messages from recent tickets for display
  const allMessages = tickets.flatMap((t) =>
    t.messages.map((m) => ({
      ...m,
      isOwn: m.sender_id === user?.id,
      ticketSubject: t.subject,
    }))
  );

  return (
    <>
      {/* Floating controls wrapper — groups help button + scroll-to-top */}
      {!open && (
        <div className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-40 flex flex-col items-center gap-2">
          <button
            onClick={() => { setOpen(true); setSent(false); }}
            className="h-14 w-14 rounded-full bg-primary shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
            aria-label="Get help"
          >
            <Avatar className="h-12 w-12 border-2 border-primary-foreground">
              <AvatarImage src={supportAgent} alt="Support" className="object-cover" />
              <AvatarFallback className="bg-primary text-primary-foreground">
                <MessageCircle className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
          </button>
          <ScrollToTopButton />
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-40 w-80 max-h-[28rem] rounded-xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-primary text-primary-foreground shrink-0">
            <Avatar className="h-8 w-8">
              <AvatarImage src={supportAgent} alt="Support" className="object-cover" />
              <AvatarFallback className="bg-primary-foreground text-primary text-xs">BP</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Business Pal Support</p>
              <p className="text-xs opacity-80">We typically reply within a few hours</p>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-primary-foreground hover:bg-primary-foreground/20" onClick={() => setOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {sent ? (
              <div className="text-center space-y-2 py-6 px-4">
                <div className="h-10 w-10 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-sm font-medium text-foreground">Message sent!</p>
                <p className="text-xs text-muted-foreground">
                  Your message has been passed to our team. We'll get back to you shortly.
                </p>
                <Button size="sm" variant="outline" onClick={() => { setSent(false); }}>
                  Send another message
                </Button>
              </div>
            ) : (
              <>
                {/* Previous messages */}
                {!isAnonymous && allMessages.length > 0 && (
                  <ScrollArea className="flex-1 max-h-48 px-3 py-2" ref={scrollRef as any}>
                    <div className="space-y-2">
                      {allMessages.map((m) => (
                        <div key={m.id} className={`flex ${m.isOwn ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs ${
                            m.isOwn
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-foreground"
                          }`}>
                            {!m.isOwn && <p className="font-semibold text-[10px] mb-0.5 opacity-70">Support</p>}
                            <p>{m.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}

                {/* Input area */}
                <div className="p-3 space-y-2 border-t border-border shrink-0">
                  {isAnonymous && (
                    <>
                      <Input
                        value={anonName}
                        onChange={(e) => setAnonName(e.target.value)}
                        placeholder="Your name"
                        className="text-sm h-8"
                      />
                      <Input
                        value={anonEmail}
                        onChange={(e) => setAnonEmail(e.target.value)}
                        placeholder="Your email"
                        type="email"
                        className="text-sm h-8"
                      />
                    </>
                  )}
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your question or issue..."
                    rows={3}
                    className="resize-none text-sm"
                  />
                  <Button
                    className="w-full"
                    onClick={handleSend}
                    disabled={!message.trim() || sending}
                    size="sm"
                  >
                    {sending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" />Send Message</>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
